import React, { useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useLicenseGate } from "../../../hooks/useLicenseGate";
import { RootState } from "../../../store";
import { useTranslation } from "react-i18next";
import { LicensePlan } from "../../../store/actions/PlanActions";
import { UPGRADE_PATH, sectionLabel } from "../../../config/entitlementKeys";
import LicenseGateModal from "../../Settings/Licensing/LicenseGateModal";

const LICENSING_ENABLED = process.env.REACT_APP_LICENSING_ENABLED === "true";

/**
 * LicenseOverlay — wraps page content and renders a semi-transparent
 * overlay when the current route's section is not licensed.
 *
 * - Content still renders (blurred/grayed) behind the overlay so users
 *   can see what they're missing — enticing them to upgrade.
 * - All interactions are blocked (pointer-events-none on content,
 *   overlay captures clicks).
 * - What sits on top is `LicenseGateModal` — the SAME component every other
 *   gate shows, so the page lock, an action gate and a server 403 all offer
 *   the same two ways out: an admin upgrades, a member requests.
 *   A global flag (__ponsLicenseOverlayActive) tells interceptors and error
 *   handlers to suppress the duplicate they would otherwise raise on top.
 */
const LicenseOverlay: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const { sectionForRoute, hasSectionAccess, loaded } = useLicenseGate();
  const addons = useSelector((state: RootState) => state.license?.addons ?? []);

  const section = sectionForRoute(location.pathname);
  const isLocked = LICENSING_ENABLED && section ? !hasSectionAccess(section) : false;

  /*
    THE LOAD-TIME RACE, HELD.

    On a cold load / hard refresh onto a section-locked page, entitlements take
    a moment (~15s observed) to resolve. During that window `hasSectionAccess`
    fails OPEN (see `useLicenseGate` — an empty entitlement set is "not yet",
    never "no"), so `isLocked` reads false and, without this, the page would
    mount, fire its data request, take a 403, and the error handler would raise
    the GLOBAL upgrade modal on top of the eventual page overlay — a double
    lock whose scrim also covers the sidebar.

    So while a SECTION-MAPPED route is still resolving, hold: render a neutral
    placeholder instead of the children, so nothing behind the gate mounts or
    fetches. Once `loaded` is true we know the answer — lock shell if locked,
    children if allowed.

    Ungated routes (no section — `/home` and the account/settings pages) are
    NEVER held: they render their children immediately, exactly as before, so
    the dashboard/home load is untouched.
  */
  const isResolvingGate = LICENSING_ENABLED && !!section && !loaded;

  // Find the addon that unlocks this section
  const matchingAddon = useMemo(() => {
    if (!isLocked || !section) return null;
    return addons.find((a: LicensePlan) => a.feature_gates?.sections?.includes(section)) ?? null;
  }, [isLocked, section, addons]);

  /* The label comes from the canonical vocabulary, like the keys do — this
     component used to carry a third copy of the map, keyed on the pre-rename
     spellings. */
  const label = section ? sectionLabel(section) : "";

  /*
    Set global flag so apis.tsx interceptors and LicenseErrorHandler suppress
    duplicate modals / error toasts on locked pages. It covers the resolving
    window too: any stray 403 raised while we hold the page should be swallowed
    just as it is on a fully locked one.

    Computed in RENDER, not only in an effect — a 403 can land before effects
    flush on a cold load, and the guard has to already read true by then.
  */
  const overlayActive = isLocked || isResolvingGate;
  (window as any).__ponsLicenseOverlayActive = overlayActive;

  useEffect(() => {
    (window as any).__ponsLicenseOverlayActive = overlayActive;
    return () => {
      (window as any).__ponsLicenseOverlayActive = false;
    };
  }, [overlayActive]);

  /*
    STILL RESOLVING — hold the page, show neither children nor a lock.

    A neutral, lightweight placeholder: no children (so nothing behind the gate
    mounts or fetches), and no lock card (we don't yet know if it's locked).
  */
  if (isResolvingGate) {
    return (
      <div
        className="relative flex-1 min-h-0"
        data-testid="license-loading-shell"
        aria-busy="true"
      >
        <div className="absolute inset-0 bg-bg" aria-hidden />
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-transparent"
            aria-hidden
          />
        </div>
      </div>
    );
  }

  /*
    NOT LOCKED, NOTHING TO SAY.

    THIS LINE WAS DELETED IN 735ef8c2 and it is the whole of the "every page
    shows the upgrade modal" bug. That commit replaced the overlay's bespoke
    card with the shared `LicenseGateModal` and rewrote the render block —
    dropping the early return with it. `isLocked` went on being computed, and
    went on driving the add-on lookup and the `__ponsLicenseOverlayActive`
    flag, so it read as live and reviewed as fine; nothing consulted it before
    drawing the scrim. Every page dimmed itself and put an upgrade modal over
    the top, on any plan, whatever the entitlements said — which is why a firm
    on PONS Full with all 21 sections saw it, and why routes with no section
    at all (`/home`) showed a modal with a blank name.

    A gate that is computed but not consulted is not a gate.
  */
  if (!isLocked) return <>{children}</>;

  /*
    THE LOCKED PAGE IS NOT RENDERED AT ALL.

    It used to render behind the scrim, dimmed and inert. The data was 403'd so
    the tables came up empty, but the page still MOUNTED: it ran its effects,
    fired its fetches, and left a working shell one DevTools node-delete away
    from looking like the product. The overlay is UX, not the security
    boundary — the server refuses regardless — but there is no reason to build
    the room behind the locked door.

    So a locked route renders the lock and nothing else. No children, no
    effects, no requests.
  */
  return (
    <div className="relative flex-1 min-h-0" data-testid="license-locked-shell">
      {/* Deliberately empty: the page it replaces is not mounted. */}
      <div className="absolute inset-0 bg-bg" aria-hidden />

      {/*
        CENTRED ON THE SCREEN, not in the content.

        The scrim is `absolute inset-0`, which on a SCROLLABLE page is as tall
        as the page's content — a long table, in the case this was reported on
        — so centring the card inside it put the card halfway down the table
        rather than halfway down the screen. The inner box is `sticky top-0`
        at one viewport high, so wherever the reader is scrolled, the card is
        centred in what they can actually see.
      */}
      {/*
        SCOPED TO THE CONTENT REGION, not the viewport.

        `<main>` and `<Sidebar />` are siblings in a flex row (`PrivateLayout`),
        so `absolute inset-0` here reaches the right-hand column only and the
        sidebar stays clickable — which is what a locked page most needs, since
        the way out of it is another section. The full-viewport lock the report
        hit was the GLOBAL modal, raised by an API 403 on a route this overlay
        did not cover; those routes are mapped now.
      */}
      <div className="absolute inset-0 z-30 bg-white/30 dark:bg-gray-900/30">
        <div className="sticky top-0 h-screen max-h-screen flex items-center
                        justify-center overflow-y-auto py-8">
        <div className="glass max-w-md w-full mx-4 rounded-xl shadow-lg my-auto"
             data-testid="license-overlay-card">
          <LicenseGateModal
            title={label}
            /* `label` cannot be blank — see `sectionLabel` — so this sentence
               can no longer render as "does not include ." */
            description={t('Your current plan does not include <strong>{{feature}}</strong>.', {
              feature: t(label),
              interpolation: { escapeValue: false },
            })}
            planName={matchingAddon?.display_name}
            category={matchingAddon ? "addon" : "base"}
            section={section || undefined}
            onViewPlans={() => history.push(UPGRADE_PATH)}
            /* There is nothing to dismiss TO — the page behind is the locked
               one. "Maybe later" takes them somewhere they can use. */
            onClose={() => history.push("/home")}
          />
        </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseOverlay;
